// Referral packet definitions + the pure scoring functions the concierge,
// the inbox and the contact profile share. Everything here is computed from
// a referral record the API returned — there is no client-side data.

export const SPECIALTIES = {
  chiro:   { label: 'Chiropractic',       service: 'Chiropractic evaluation & treatment plan' },
  imaging: { label: 'Diagnostic imaging', service: 'MRI / diagnostic imaging' },
  ortho:   { label: 'Orthopedics',        service: 'Orthopedic consultation & treatment' },
  pt:      { label: 'Physical therapy',   service: 'Physical therapy plan of care' },
  neuro:   { label: 'Neurology',          service: 'Neurological evaluation' },
};

// The eight items a provider needs before treating on a lien. Keys match
// the server's DOC_PATTERNS / packet shape.
export const PACKET_ITEMS = [
  { key: 'lop_letter', label: 'Signed LOP on firm letterhead', hint: 'Attorney signature · client name · date of loss', kind: 'doc' },
  { key: 'lien_ack', label: 'Patient lien acknowledgment', hint: 'Signed by the patient', kind: 'doc' },
  { key: 'referral_rx', label: 'Referral / prescription', hint: 'Treating provider with NPI + signature', kind: 'doc' },
  { key: 'pip_log', label: 'PIP exhaustion log / benefits letter', hint: 'Documents why this is LOP, not PIP', kind: 'doc' },
  { key: 'insurance_facts', label: 'At-fault carrier & BI limits', hint: 'Carrier · claim # · adjuster · BI policy limits', kind: 'fields' },
  { key: 'police_report', label: 'Police / crash report', hint: 'Establishes liability posture', kind: 'doc' },
  { key: 'hipaa_auth', label: 'HIPAA authorization', hint: 'Records release back to the firm', kind: 'doc' },
  { key: 'demographics', label: 'Patient demographics & intake', hint: 'Name · DOB · contact', kind: 'fields' },
];

export function itemComplete(referral, item) {
  if (item.kind === 'fields') {
    if (item.key === 'insurance_facts') {
      const i = referral.insurance || {};
      return !!(i.carrier && i.claim_number && i.bi_per_person);
    }
    if (item.key === 'demographics') {
      const c = referral.client || referral.client_snapshot || {};
      return !!(c.name && c.dob && c.phone);
    }
    return false;
  }
  return !!referral.packet?.[item.key]?.attached;
}

export function completeness(referral) {
  const done = PACKET_ITEMS.filter((it) => itemComplete(referral, it));
  return { done: done.length, total: PACKET_ITEMS.length, missing: PACKET_ITEMS.filter((it) => !itemComplete(referral, it)) };
}

// The underwriting read: can this case actually pay the lien? Inputs are the
// BI limit, liens ahead, liability posture and PIP documentation — all from
// the assembled file. Unknowns count against the score, never for it.
export function scoreCollectability(referral) {
  const ins = referral.insurance || {};
  const liens = referral.liens || {};
  const bi = Number(ins.bi_per_person) || 0;
  const aheadAmt = Number(liens.ahead_amount) || 0;
  const aheadCount = Number(liens.ahead_count) || 0;
  const headroom = bi - aheadAmt;
  const liability = ins.liability || 'unknown';

  const reasons = [
    { ok: bi >= 50000, text: bi > 0 ? `BI policy limit ${fmtMoney(bi)} per person` : 'BI policy limits not on file' },
    { ok: aheadCount === 0, text: aheadCount === 0 ? 'No liens ahead recorded' : `${aheadCount} lien${aheadCount === 1 ? '' : 's'} ahead totaling ${fmtMoney(aheadAmt)}` },
    { ok: headroom >= 25000, text: `Estimated headroom after prior liens: ${fmtMoney(headroom)}` },
    { ok: liability === 'clear', text: liability === 'clear' ? 'Liability clear' : liability === 'shared' ? 'Comparative fault likely — shared liability' : liability === 'disputed' ? 'Liability disputed by carrier' : 'Liability posture not recorded' },
    { ok: ins.pip_status === 'exhausted', text: ins.pip_status === 'exhausted' ? 'PIP exhausted and documented' : ins.pip_status === 'na' ? 'No PIP coverage' : 'PIP status not confirmed' },
  ];

  let tier = 'green';
  if (bi === 0 || headroom < 7500 || (liability === 'disputed' && headroom < 25000)) tier = 'red';
  else if (headroom < 25000 || liability !== 'clear' || aheadCount >= 2 || ins.pip_status !== 'exhausted') tier = 'yellow';

  const label = tier === 'green' ? 'Strong' : tier === 'yellow' ? 'Review' : 'High risk';
  return { tier, label, headroom, reasons };
}

// One view of a referral's packet for every screen. Referrals sent after
// migration 056 carry `billing` (payer + that payer's required items + a
// funding read from the server); older rows fall back to the LOP list.
export function packetView(referral) {
  const b = referral?.billing;
  if (b && Array.isArray(b.items)) {
    const required = b.items.filter((i) => i.required !== false);
    return {
      items: b.items,
      done: b.done ?? required.filter((i) => i.done).length,
      total: b.total ?? required.length,
      risk: b.risk || null,
      billing: b,
      legacy: false,
    };
  }
  const r = referral || {};
  const c = completeness(r);
  const s = scoreCollectability(r);
  return {
    items: PACKET_ITEMS.map((it) => ({ ...it, required: true, done: itemComplete(r, it), file: r.packet?.[it.key]?.file || null })),
    done: c.done,
    total: c.total,
    risk: { tier: s.tier, label: s.label, reasons: s.reasons },
    billing: null,
    legacy: true,
  };
}

export const MODE_LABEL = { pip: 'PIP (no-fault)', medpay: 'MedPay', health: 'Health insurance', lop: 'Letter of Protection', um_uim: 'UM/UIM', self_pay: 'Self-pay', none: 'No payer on file' };

export function fmtMoney(n) {
  const v = Number(n) || 0;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString()}`;
}

export function fmtSlot(date) {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
